import { useEffect, useRef, useState, type ChangeEvent, type FocusEvent } from 'react';

/** Converte minutos (decimal) em horas, minutos e segundos. */
export function minutosParaHms(minutos: number): { h: number; m: number; s: number } {
  const totalSeg = Math.max(0, Math.round((Number(minutos) || 0) * 60));
  return {
    h: Math.floor(totalSeg / 3600),
    m: Math.floor((totalSeg % 3600) / 60),
    s: totalSeg % 60,
  };
}

/** Converte H + M + S em minutos (decimal). Minutos podem passar de 59 (ex.: 90 → 1h30). */
export function hmsParaMinutos(h: number, m: number, s: number): number {
  let hh = Math.max(0, Math.floor(Number(h) || 0));
  let mm = Math.max(0, Math.floor(Number(m) || 0));
  let ss = Math.min(59, Math.max(0, Math.floor(Number(s) || 0)));
  if (mm >= 60) {
    hh += Math.floor(mm / 60);
    mm %= 60;
  }
  return (hh * 3600 + mm * 60 + ss) / 60;
}

interface InputTempoHmsProps {
  minutos: number;
  onChange: (minutos: number) => void;
  className?: string;
  compact?: boolean;
  disabled?: boolean;
}

/**
 * Entrada de tempo com 3 campos numéricos (hora : minuto : segundo).
 * Evita o problema do campo único HH:MM:SS que reformatava a cada tecla.
 */
export function InputTempoHms({ minutos, onChange, className = '', compact, disabled }: InputTempoHmsProps) {
  const alvo = minutosParaHms(minutos);
  const [h, setH] = useState(String(alvo.h));
  const [m, setM] = useState(String(alvo.m));
  const [s, setS] = useState(String(alvo.s));
  const editandoRef = useRef(false);

  useEffect(() => {
    if (editandoRef.current) return;
    const next = minutosParaHms(minutos);
    setH(String(next.h));
    setM(String(next.m));
    setS(String(next.s));
  }, [minutos]);

  const emitir = (nh: string, nm: string, ns: string) => {
    onChange(hmsParaMinutos(Number(nh) || 0, Number(nm) || 0, Number(ns) || 0));
  };

  const campoClass = [
    compact
      ? 'w-10 min-w-[2.5rem] px-1 py-1 text-center text-sm font-mono border border-[#009b3a]/30 rounded'
      : 'w-12 min-w-[3rem] px-1.5 py-1.5 text-center text-sm font-mono border border-[#009b3a]/30 rounded',
    disabled ? 'bg-neutral-100 text-neutral-500 cursor-not-allowed' : 'bg-white',
  ].join(' ');

  const normalizarESalvar = (nh: string, nm: string, ns: string) => {
    const normalizado = minutosParaHms(hmsParaMinutos(Number(nh) || 0, Number(nm) || 0, Number(ns) || 0));
    const hs = String(normalizado.h);
    const ms = String(normalizado.m);
    const ss = String(normalizado.s);
    setH(hs);
    setM(ms);
    setS(ss);
    emitir(hs, ms, ss);
  };

  const onSaiGrupo = (e: FocusEvent<HTMLDivElement>) => {
    const proximo = e.relatedTarget as Node | null;
    if (proximo && e.currentTarget.contains(proximo)) return;
    editandoRef.current = false;
    normalizarESalvar(h, m, s);
  };

  const onCampo =
    (parte: 'h' | 'm' | 's') => (e: ChangeEvent<HTMLInputElement>) => {
      editandoRef.current = true;
      const raw = e.target.value.replace(/\D/g, '').slice(0, parte === 's' ? 2 : 4);
      const nh = parte === 'h' ? raw : h;
      const nm = parte === 'm' ? raw : m;
      const ns = parte === 's' ? raw : s;
      if (parte === 'h') setH(raw);
      if (parte === 'm') setM(raw);
      if (parte === 's') setS(raw);
      emitir(nh, nm, ns);
    };

  return (
    <div
      className={`inline-flex items-center gap-0.5 ${className}`}
      title="Hora : minuto : segundo"
      onFocusCapture={() => {
        editandoRef.current = true;
      }}
      onBlurCapture={onSaiGrupo}
    >
      <input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        value={h}
        onChange={onCampo('h')}
        onWheel={(e) => (e.target as HTMLInputElement).blur()}
        className={campoClass}
        aria-label="Horas"
        placeholder="0"
        disabled={disabled}
        readOnly={disabled}
      />
      <span className="text-neutral-500 font-bold text-xs">:</span>
      <input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        value={m}
        onChange={onCampo('m')}
        onWheel={(e) => (e.target as HTMLInputElement).blur()}
        className={campoClass}
        aria-label="Minutos"
        placeholder="0"
        disabled={disabled}
        readOnly={disabled}
      />
      <span className="text-neutral-500 font-bold text-xs">:</span>
      <input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        value={s}
        onChange={onCampo('s')}
        onWheel={(e) => (e.target as HTMLInputElement).blur()}
        className={campoClass}
        aria-label="Segundos"
        placeholder="0"
        disabled={disabled}
        readOnly={disabled}
      />
    </div>
  );
}
